import { useQuery } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { createPublicClient, http, formatUnits } from 'viem'
import { base } from 'viem/chains'
import { AAVE_V3_BASE_TOKENS } from '../data/tokens'

// Aave V3 Pool ABI for reserve data
const AAVE_POOL_ABI = [
  {
    "inputs": [
      {"name": "asset", "type": "address"}
    ],
    "name": "getReserveData",
    "outputs": [
      {"name": "configuration", "type": "uint256"},
      {"name": "liquidityIndex", "type": "uint128"},
      {"name": "currentLiquidityRate", "type": "uint128"},
      {"name": "variableBorrowIndex", "type": "uint128"},
      {"name": "currentVariableBorrowRate", "type": "uint128"},
      {"name": "currentStableBorrowRate", "type": "uint128"},
      {"name": "lastUpdateTimestamp", "type": "uint40"},
      {"name": "id", "type": "uint16"},
      {"name": "aTokenAddress", "type": "address"},
      {"name": "stableDebtTokenAddress", "type": "address"},
      {"name": "variableDebtTokenAddress", "type": "address"},
      {"name": "interestRateStrategyAddress", "type": "address"},
      {"name": "accruedToTreasury", "type": "uint128"},
      {"name": "unbacked", "type": "uint128"},
      {"name": "isolationModeTotalDebt", "type": "uint128"}
    ],
    "stateMutability": "view",
    "type": "function"
  }
] as const

// ERC20 ABI for aToken and debt token balances
const ERC20_ABI = [
  {
    "inputs": [
      {"name": "account", "type": "address"}
    ],
    "name": "balanceOf",
    "outputs": [
      {"name": "", "type": "uint256"}
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "decimals",
    "outputs": [
      {"name": "", "type": "uint8"}
    ],
    "stateMutability": "view",
    "type": "function"
  }
] as const

// Aave V3 Pool address on Base mainnet
const AAVE_POOL_ADDRESS = '0xA238Dd80C259a72e81d7e4664a9801593F98d1c5'

// Create public client for Base mainnet
const publicClient = createPublicClient({
  chain: base,
  transport: http()
})

export interface UserReserveData {
  tokenAddress: string
  symbol: string
  suppliedAmount: string
  borrowedAmount: string
  availableToWithdraw: string
  availableToRepay: string
  usageAsCollateralEnabled: boolean
}

async function fetchUserReserves(userAddress: string, aaveData?: any[]): Promise<UserReserveData[]> {
  try {
    console.log('[user-reserves] Fetching user reserve data for:', userAddress)
    
    if (!aaveData || aaveData.length === 0) {
      console.log('[user-reserves] No aaveData available, returning empty reserves')
      return AAVE_V3_BASE_TOKENS.map(token => ({
        tokenAddress: token.address,
        symbol: token.symbol,
        suppliedAmount: "0",
        borrowedAmount: "0",
        availableToWithdraw: "0",
        availableToRepay: "0",
        usageAsCollateralEnabled: false
      }))
    }

    const userReserves: UserReserveData[] = []

    // First, get a quick overview of which tokens might have positions
    // by checking if they're in the aaveData (which indicates they have some activity)
    const activeTokens = aaveData.filter(reserve => 
      AAVE_V3_BASE_TOKENS.some(token => 
        token.address.toLowerCase() === reserve.underlyingAsset.toLowerCase()
      )
    ).map(reserve => 
      AAVE_V3_BASE_TOKENS.find(token => 
        token.address.toLowerCase() === reserve.underlyingAsset.toLowerCase()
      )
    ).filter(Boolean)

    console.log(`[user-reserves] Found ${activeTokens.length} potentially active tokens, will fetch detailed data for all tokens`)

    // Helper function to make RPC calls with retry logic
    const makeRpcCall = async (callFn: () => Promise<any>, retries = 3): Promise<any> => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          return await callFn()
        } catch (error: any) {
          if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
            if (attempt < retries) {
              const delay = Math.pow(2, attempt) * 1000 // Exponential backoff: 2s, 4s, 8s
              console.log(`[user-reserves] Rate limited, retrying in ${delay}ms (attempt ${attempt}/${retries})`)
              await new Promise(resolve => setTimeout(resolve, delay))
              continue
            }
          }
          throw error
        }
      }
    }

    // Fetch user reserve data for each token
    for (let i = 0; i < AAVE_V3_BASE_TOKENS.length; i++) {
      const token = AAVE_V3_BASE_TOKENS[i]
      try {
        console.log(`[user-reserves] Fetching data for ${token.symbol} (${token.address})`)
        
        // Add longer delay between tokens to avoid rate limiting
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)) // 1 second delay between tokens
        }
        
        // Get reserve data to get the aToken and debt token addresses
        const reserveData = await makeRpcCall(() => 
          publicClient.readContract({
            address: AAVE_POOL_ADDRESS,
            abi: AAVE_POOL_ABI,
            functionName: 'getReserveData',
            args: [token.address as `0x${string}`]
          })
        )

        const [
          configuration,
          liquidityIndex,
          currentLiquidityRate,
          variableBorrowIndex,
          currentVariableBorrowRate,
          currentStableBorrowRate,
          lastUpdateTimestamp,
          id,
          aTokenAddress,
          stableDebtTokenAddress,
          variableDebtTokenAddress,
          interestRateStrategyAddress,
          accruedToTreasury,
          unbacked,
          isolationModeTotalDebt
        ] = reserveData

        // Add delay between calls
        await new Promise(resolve => setTimeout(resolve, 500)) // 500ms delay

        // Get aToken balance (this represents the user's supplied amount)
        const aTokenBalance = await makeRpcCall(() =>
          publicClient.readContract({
            address: aTokenAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [userAddress as `0x${string}`]
          })
        )

        // Add delay between calls
        await new Promise(resolve => setTimeout(resolve, 500)) // 500ms delay

        // Get stable debt token balance
        const stableDebtBalance = await makeRpcCall(() =>
          publicClient.readContract({
            address: stableDebtTokenAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [userAddress as `0x${string}`]
          })
        )

        // Add delay between calls
        await new Promise(resolve => setTimeout(resolve, 500)) // 500ms delay

        // Get variable debt token balance
        const variableDebtBalance = await makeRpcCall(() =>
          publicClient.readContract({
            address: variableDebtTokenAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [userAddress as `0x${string}`]
          })
        )

        // Calculate actual supplied amount using liquidity index
        // The aToken balance needs to be adjusted by the liquidity index to get the underlying amount
        const suppliedAmountWei = (aTokenBalance * liquidityIndex) / BigInt(10 ** 27)
        const suppliedAmount = formatUnits(suppliedAmountWei, token.decimals || 18)

        // Calculate total borrowed amount (stable + variable debt)
        const totalBorrowedWei = stableDebtBalance + variableDebtBalance
        const borrowedAmount = formatUnits(totalBorrowedWei, token.decimals || 18)

        // Available to withdraw is the same as supplied amount (minus any health factor considerations)
        const availableToWithdraw = suppliedAmount

        // Available to repay is the total borrowed amount
        const availableToRepay = borrowedAmount

        // Check if usage as collateral is enabled (from configuration)
        const usageAsCollateralEnabled = (configuration & BigInt(1)) === BigInt(1)

        console.log(`[user-reserves] ${token.symbol}:`)
        console.log(`[user-reserves]   aToken balance: ${aTokenBalance.toString()}`)
        console.log(`[user-reserves]   liquidity index: ${liquidityIndex.toString()}`)
        console.log(`[user-reserves]   Supplied: ${suppliedAmount}`)
        console.log(`[user-reserves]   Stable debt: ${stableDebtBalance.toString()}`)
        console.log(`[user-reserves]   Variable debt: ${variableDebtBalance.toString()}`)
        console.log(`[user-reserves]   Borrowed: ${borrowedAmount}`)
        console.log(`[user-reserves]   Available to withdraw: ${availableToWithdraw}`)
        console.log(`[user-reserves]   Available to repay: ${availableToRepay}`)
        console.log(`[user-reserves]   Usage as collateral: ${usageAsCollateralEnabled}`)

        userReserves.push({
          tokenAddress: token.address,
          symbol: token.symbol,
          suppliedAmount,
          borrowedAmount,
          availableToWithdraw,
          availableToRepay,
          usageAsCollateralEnabled
        })

      } catch (error) {
        console.warn(`[user-reserves] Failed to fetch data for ${token.symbol}:`, error)
        
        // Add empty data for this token
        userReserves.push({
          tokenAddress: token.address,
          symbol: token.symbol,
          suppliedAmount: "0",
          borrowedAmount: "0",
          availableToWithdraw: "0",
          availableToRepay: "0",
          usageAsCollateralEnabled: false
        })
      }
    }

    console.log('[user-reserves] Fetched user reserves for', userReserves.length, 'tokens')
    return userReserves

  } catch (error) {
    console.error('[user-reserves] Error fetching user reserves:', error)
    
    // Return empty reserves on error
    return AAVE_V3_BASE_TOKENS.map(token => ({
      tokenAddress: token.address,
      symbol: token.symbol,
      suppliedAmount: "0",
      borrowedAmount: "0",
      availableToWithdraw: "0",
      availableToRepay: "0",
      usageAsCollateralEnabled: false
    }))
  }
}

export function useUserReserves(aaveData?: any[], enabled = false) {
  const { address, isConnected } = useAccount()

  return useQuery({
    queryKey: ['user-reserves', address, aaveData],
    queryFn: () => fetchUserReserves(address!, aaveData),
    enabled: enabled && !!address && isConnected && !!aaveData && aaveData.length > 0,
    staleTime: 300000, // 5 minutes - much longer to reduce RPC calls
    refetchInterval: 600000, // 10 minutes - very long interval
    retry: 1, // Reduce retries to avoid overwhelming RPC
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

// Helper to get reserve data for a specific token
export function useTokenReserve(tokenAddress: string, aaveData?: any[]) {
  const { data: userReserves, isLoading, error } = useUserReserves(aaveData)

  const tokenReserve = userReserves?.find(reserve => 
    reserve.tokenAddress.toLowerCase() === tokenAddress.toLowerCase()
  )

  return {
    reserve: tokenReserve,
    isLoading,
    error
  }
}
